<template>
  <div class="min-h-screen bg-slate-100 p-4">
    <div class="mx-auto max-w-7xl space-y-4">
      <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-lg font-semibold text-slate-800">机构账户展示权限</h2>
            <p class="mt-1 text-sm text-slate-500">为每个机构登录账户独立设置 SleepReport 原版或体验版。</p>
          </div>
          <div class="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <el-button type="primary" @click="openCreateDialog">开设账号</el-button>
            <el-input
              v-model="search"
              clearable
              placeholder="搜索账号、机构或名称"
              class="min-w-0 sm:w-80"
              @keyup.enter="loadAccounts(1)"
            />
            <el-button type="primary" @click="loadAccounts(1)">搜索</el-button>
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <el-table :data="accounts" v-loading="loading" border stripe>
          <el-table-column prop="manager_org" label="机构" min-width="220">
            <template #default="{ row }">{{ row.manager_org || '-' }}</template>
          </el-table-column>
          <el-table-column prop="nickname" label="账户名称" min-width="150">
            <template #default="{ row }">{{ row.nickname || '-' }}</template>
          </el-table-column>
          <el-table-column prop="phone_number" label="登录账号" min-width="150" />
          <el-table-column prop="role" label="角色" width="110" />
          <el-table-column label="展示版本" min-width="300">
            <template #default="{ row }">
              <el-radio-group
                :model-value="row.sleepreport_display_mode"
                :disabled="savingUserId === row.user_id"
                @change="mode => saveMode(row, mode)"
              >
                <el-radio-button label="original">原版（默认）</el-radio-button>
                <el-radio-button label="cohort">体验版</el-radio-button>
              </el-radio-group>
            </template>
          </el-table-column>
        </el-table>

        <div class="mt-5 flex justify-center">
          <el-pagination
            v-model:current-page="page"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="total"
            @current-change="loadAccounts"
          />
        </div>
      </section>
    </div>

    <el-dialog v-model="createDialogVisible" title="开设机构账号" width="min(92vw, 520px)">
      <el-form label-position="top">
        <el-form-item label="机构名称" required>
          <el-input
            v-model="createForm.institution_name"
            maxlength="100"
            show-word-limit
            placeholder="请输入机构全称"
          />
        </el-form-item>
        <el-form-item label="展示版本">
          <el-radio-group v-model="createForm.mode">
            <el-radio-button label="original">原版（默认）</el-radio-button>
            <el-radio-button label="cohort">体验版</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="实时监测">
          <el-checkbox v-model="createForm.realtime_monitoring_enabled">为该机构启用实时监测</el-checkbox>
          <p class="mt-1 text-sm text-slate-500">默认不启用；该权限对整个机构生效，与账户展示版本独立。</p>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="createAccount">确认开设</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="credentialDialogVisible" title="账号已开设" width="min(92vw, 520px)">
      <el-descriptions v-if="createdAccount" :column="1" border>
        <el-descriptions-item label="机构">{{ createdAccount.manager_org }}</el-descriptions-item>
        <el-descriptions-item label="登录账号">{{ createdAccount.phone_number }}</el-descriptions-item>
        <el-descriptions-item label="默认密码">{{ createdAccount.default_password }}</el-descriptions-item>
        <el-descriptions-item label="展示版本">
          {{ createdAccount.sleepreport_display_mode === 'cohort' ? '体验版' : '原版' }}
        </el-descriptions-item>
        <el-descriptions-item label="实时监测">
          {{ createdAccount.realtime_monitoring_enabled === true ? '已启用' : '未启用' }}
        </el-descriptions-item>
      </el-descriptions>
      <p class="mt-3 text-sm text-slate-500">默认密码为登录账号后六位，请及时交给机构联系人。</p>
      <template #footer>
        <el-button type="primary" @click="credentialDialogVisible = false">我知道了</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { createAxiosInstance } from '@/plugins/axios.js'

const accounts = ref([])
const loading = ref(false)
const savingUserId = ref('')
const search = ref('')
const page = ref(1)
const pageSize = 20
const total = ref(0)
const createDialogVisible = ref(false)
const credentialDialogVisible = ref(false)
const creating = ref(false)
const createdAccount = ref(null)
const createForm = ref({ institution_name: '', mode: 'original', realtime_monitoring_enabled: false })

const openCreateDialog = () => {
  createForm.value = { institution_name: '', mode: 'original', realtime_monitoring_enabled: false }
  createDialogVisible.value = true
}

const createAccount = async () => {
  const institutionName = createForm.value.institution_name.trim()
  if (!institutionName) {
    ElMessage.warning('请输入机构名称')
    return
  }

  creating.value = true
  try {
    const axios = createAxiosInstance('数据服务器')
    const response = await axios.post('/sleepreport/accounts', {
      institution_name: institutionName,
      mode: createForm.value.mode,
      realtime_monitoring_enabled: createForm.value.realtime_monitoring_enabled === true,
    })
    createdAccount.value = response.data
    createDialogVisible.value = false
    credentialDialogVisible.value = true
    search.value = institutionName
    await loadAccounts(1)
  } catch (error) {
    console.error('开设机构账号失败:', error)
    const detail = error?.response?.data?.detail
    ElMessage.error(error?.response?.status === 409
      ? '机构账号或实时权限已存在，本次未修改配置。请联系管理员管理已有机构设置。'
      : detail || '开设机构账号失败')
  } finally {
    creating.value = false
  }
}

const loadAccounts = async (targetPage = page.value) => {
  loading.value = true
  try {
    const axios = createAxiosInstance('数据服务器')
    const response = await axios.get('/sleepreport/accounts', {
      params: { search: search.value.trim(), page: targetPage, page_size: pageSize },
    })
    accounts.value = response.data?.items || []
    total.value = response.data?.total || 0
    page.value = response.data?.page || targetPage
  } catch (error) {
    console.error('加载机构账户失败:', error)
    ElMessage.error(error?.response?.data?.detail || '加载机构账户失败')
  } finally {
    loading.value = false
  }
}

const saveMode = async (row, mode) => {
  const previousMode = row.sleepreport_display_mode
  row.sleepreport_display_mode = mode
  savingUserId.value = row.user_id
  try {
    const axios = createAxiosInstance('数据服务器')
    const response = await axios.post(
      `/sleepreport/accounts/${encodeURIComponent(row.user_id)}/display-mode`,
      { mode },
    )
    row.sleepreport_display_mode = response.data?.sleepreport_display_mode || mode
    ElMessage.success(`已将“${row.nickname || row.phone_number}”设置为${mode === 'cohort' ? '体验版' : '原版'}`)
  } catch (error) {
    row.sleepreport_display_mode = previousMode
    console.error('更新展示权限失败:', error)
    ElMessage.error(error?.response?.data?.detail || '更新展示权限失败')
  } finally {
    savingUserId.value = ''
  }
}

onMounted(() => loadAccounts(1))
</script>
